'use client';

import React, { useState, useMemo } from 'react';
import AppLayout from '@/components/AppLayout';
import { ChevronLeft, ChevronRight, Package } from 'lucide-react';

interface Kit {
  id: string;
  serialNo: string;
  ccNo: string;
  kitName: string;
  expiry: string;
  ccName: string;
  status: 'active' | 'near-3m' | 'near-6m' | 'expired' | 'short';
}

// Simulated logged-in customer
const CUSTOMER = {
  name: 'AIIMS PATNA',
  id: 'BHISHM/AIIMS/MED/PTN',
  role: 'Store Keeper',
  totalAllocation: 1089,
};

// Generate 1089 kits scoped to this customer
function generateCustomerKits(): Kit[] {
  const kitNames = [
    'BLOOD GROUPING KIT', 'MALARIA TEST KIT', 'PREGNANCY TEST KIT', 'DENGUE TEST KIT',
    'TROPONIN TEST KIT', 'TYPHOID TEST KIT', 'HIV TEST KIT', 'CHOLERA TEST KIT',
    'HEPATITIS B KIT', 'LEPTOSPIROSIS KIT', 'SCRUB TYPHUS KIT', 'CHIKUNGUNYA KIT',
    'URINE STRIP KIT', 'STOOL TEST KIT', 'GLUCOSE TEST KIT', 'SEARCH LIGHT KIT',
    'WOUND CARE KIT', 'SURGICAL DRESSING KIT', 'IV CANNULA KIT', 'OXYGEN MASK KIT',
    'SUTURE KIT', 'SPLINT KIT', 'BURN CARE KIT', 'TRAUMA CARE KIT',
    'SNAKE BITE KIT', 'ANTIDOTE KIT', 'RESUSCITATION KIT', 'DEFIBRILLATOR PAD KIT',
    'BLOOD PRESSURE KIT', 'PULSE OXIMETER KIT',
  ];
  const ccNames = [
    'LAB CARD TEST SET', 'RAPID DIAGNOSTIC SET', 'VECTOR DISEASE SET',
    'GENERAL DIAGNOSTIC SET', 'METABOLIC PANEL SET', 'EMERGENCY CAMP LIGHT SET',
    'SURGICAL INSTRUMENT SET', 'TRAUMA RESPONSE SET', 'CRITICAL CARE SET',
    'FIELD MEDICAL SET',
  ];
  const depts = ['TECH', 'MED', 'SURG', 'LAB', 'ICU'];

  const statusPool: Kit['status'][] = [];
  for (let i = 0; i < 721; i++) statusPool.push('active');
  for (let i = 0; i < 3; i++) statusPool.push('near-3m');
  for (let i = 0; i < 17; i++) statusPool.push('near-6m');
  for (let i = 0; i < 149; i++) statusPool.push('expired');
  for (let i = 0; i < 69; i++) statusPool.push('short');
  while (statusPool.length < 1089) statusPool.push('active');

  for (let i = statusPool.length - 1; i > 0; i--) {
    const j = (i * 1103515245 + 12345) % statusPool.length;
    [statusPool[i], statusPool[j]] = [statusPool[j], statusPool[i]];
  }

  const today = new Date('2026-07-01');

  return Array.from({ length: 1089 }, (_, idx) => {
    let i = idx + 1;
    const status = statusPool[idx];
    const dept = depts[i % depts.length];
    const num = String(Math.floor(i / 10) + 1).padStart(2, '0');
    const serialNo = `BHISHM/AIIMS/${dept}/PTN/${num}`;
    const ccNo = `${(i % 30) + 1}/25`;
    const kitName = kitNames[i % kitNames.length];
    const ccName = ccNames[i % ccNames.length];

    let expiry: string;
    if (status === 'short') {
      expiry = 'NA';
    } else if (status === 'expired') {
      const d = new Date(today);
      d.setDate(d.getDate() - ((i % 365) + 1));
      expiry = d.toISOString().split('T')[0];
    } else if (status === 'near-3m') {
      const d = new Date(today);
      d.setDate(d.getDate() + ((i % 89) + 1));
      expiry = d.toISOString().split('T')[0];
    } else if (status === 'near-6m') {
      const d = new Date(today);
      d.setDate(d.getDate() + 91 + ((i % 89) + 1));
      expiry = d.toISOString().split('T')[0];
    } else {
      const d = new Date(today);
      d.setDate(d.getDate() + 181 + ((i % 365) + 1));
      expiry = d.toISOString().split('T')[0];
    }

    return { id: `ckit-${String(i).padStart(4, '0')}`, serialNo, ccNo, kitName, expiry, ccName, status };
  });
}

const CUSTOMER_KITS: Kit[] = generateCustomerKits();

type TabKey = 'all' | 'near-3m' | 'near-6m' | 'expired' | 'short';

const TABS: { key: TabKey; label: string; color: string; activeStyle: React.CSSProperties; badgeStyle: React.CSSProperties }[] = [
  {
    key: 'all',
    label: 'All Kits',
    color: '#0369a1',
    activeStyle: { background: '#0369a1', color: '#fff', borderColor: '#0369a1' },
    badgeStyle: { background: 'rgba(3,105,161,0.12)', color: '#0369a1' },
  },
  {
    key: 'near-3m',
    label: 'Nearby Expiry 3M',
    color: '#b45309',
    activeStyle: { background: '#b45309', color: '#fff', borderColor: '#b45309' },
    badgeStyle: { background: 'rgba(180,83,9,0.12)', color: '#b45309' },
  },
  {
    key: 'near-6m',
    label: 'Nearby Expiry 6M',
    color: '#d97706',
    activeStyle: { background: '#d97706', color: '#fff', borderColor: '#d97706' },
    badgeStyle: { background: 'rgba(217,119,6,0.12)', color: '#d97706' },
  },
  {
    key: 'expired',
    label: 'Expired Kits',
    color: '#dc2626',
    activeStyle: { background: '#dc2626', color: '#fff', borderColor: '#dc2626' },
    badgeStyle: { background: 'rgba(220,38,38,0.12)', color: '#dc2626' },
  },
  {
    key: 'short',
    label: 'Kits Shorts',
    color: '#ea580c',
    activeStyle: { background: '#ea580c', color: '#fff', borderColor: '#ea580c' },
    badgeStyle: { background: 'rgba(234,88,12,0.12)', color: '#ea580c' },
  },
];

const ITEMS_PER_PAGE_OPTIONS = [8, 12, 16, 24];

function getStatusBadge(status: Kit['status']) {
  const map: Record<Kit['status'], { label: string; bg: string; color: string; border: string }> = {
    active: { label: 'Active', bg: 'rgba(22,163,74,0.1)', color: '#16a34a', border: 'rgba(22,163,74,0.25)' },
    'near-3m': { label: 'Near Expiry', bg: 'rgba(180,83,9,0.1)', color: '#b45309', border: 'rgba(180,83,9,0.25)' },
    'near-6m': { label: 'Near Expiry', bg: 'rgba(217,119,6,0.08)', color: '#d97706', border: 'rgba(217,119,6,0.22)' },
    expired: { label: 'Expired', bg: 'rgba(220,38,38,0.1)', color: '#dc2626', border: 'rgba(220,38,38,0.25)' },
    short: { label: 'Short', bg: 'rgba(234,88,12,0.1)', color: '#ea580c', border: 'rgba(234,88,12,0.25)' },
  };
  return map[status];
}

function formatExpiry(expiry: string): string {
  if (expiry === 'NA') return 'NA';
  const parts = expiry.split('-');
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return expiry;
}

function getExpiryTextColor(status: Kit['status']): string {
  if (status === 'expired') return '#dc2626';
  if (status === 'near-3m') return '#b45309';
  if (status === 'near-6m') return '#d97706';
  if (status === 'short') return '#94a3b8';
  return '#16a34a';
}

export default function MyKitsDashboardPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [search, setSearch] = useState('');

  const tabCounts: Record<TabKey, number> = useMemo(() => ({
    all: CUSTOMER_KITS.length,
    'near-3m': CUSTOMER_KITS.filter((k) => k.status === 'near-3m').length,
    'near-6m': CUSTOMER_KITS.filter((k) => k.status === 'near-6m').length,
    expired: CUSTOMER_KITS.filter((k) => k.status === 'expired').length,
    short: CUSTOMER_KITS.filter((k) => k.status === 'short').length,
  }), []);

  const filteredKits = useMemo(() => {
    let kits = activeTab === 'all' ? CUSTOMER_KITS : CUSTOMER_KITS.filter((k) => k.status === activeTab);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      kits = kits.filter(
        (k) =>
          k.kitName.toLowerCase().includes(q) ||
          k.serialNo.toLowerCase().includes(q) ||
          k.ccNo.toLowerCase().includes(q) ||
          k.ccName.toLowerCase().includes(q)
      );
    }
    return kits;
  }, [activeTab, search]);

  const totalKits = filteredKits.length;
  const totalPages = Math.ceil(totalKits / itemsPerPage);
  const start = (page - 1) * itemsPerPage;
  const end = Math.min(start + itemsPerPage, totalKits);
  const visibleKits = filteredKits.slice(start, end);

  function handleTabChange(key: TabKey) {
    setActiveTab(key);
    setPage(1);
  }

  const activeTabMeta = TABS.find((t) => t.key === activeTab)!;

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');
    for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) pages.push(p);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [page, totalPages]);

  // Summary stats
  const activeCount = tabCounts.all - tabCounts['near-3m'] - tabCounts['near-6m'] - tabCounts.expired - tabCounts.short;

  return (
    <AppLayout activePath="/my-kits">
      <div className="px-4 lg:px-6 xl:px-8 py-5 max-w-screen-2xl mx-auto space-y-5">

        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-700" style={{ color: '#0f2744' }}>My Kit Inventory</h1>
            <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
              {CUSTOMER.name} — Allocated: {CUSTOMER.totalAllocation.toLocaleString()} kits
            </p>
          </div>
          {/* Customer badge */}
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-xl self-start sm:self-auto"
            style={{ background: '#f0f7ff', border: '1px solid rgba(3,105,161,0.2)' }}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-700 text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #0369a1, #0ea5e9)' }}
            >
              {CUSTOMER.name.charAt(0)}
            </div>
            <div>
              <p className="text-xs font-600 leading-tight" style={{ color: '#0f2744' }}>{CUSTOMER.name}</p>
              <p className="text-[10px] leading-tight" style={{ color: '#64748b' }}>{CUSTOMER.role}</p>
            </div>
          </div>
        </div>

        {/* Summary stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { label: 'Total Kits', value: CUSTOMER.totalAllocation, color: '#0369a1', bg: 'rgba(3,105,161,0.07)', border: 'rgba(3,105,161,0.18)', icon: <Package size={15} /> },
            { label: 'Active', value: activeCount, color: '#16a34a', bg: 'rgba(22,163,74,0.07)', border: 'rgba(22,163,74,0.18)', icon: null },
            { label: 'Near Expiry (3M)', value: tabCounts['near-3m'], color: '#b45309', bg: 'rgba(180,83,9,0.07)', border: 'rgba(180,83,9,0.18)', icon: null },
            { label: 'Expired', value: tabCounts.expired, color: '#dc2626', bg: 'rgba(220,38,38,0.07)', border: 'rgba(220,38,38,0.18)', icon: null },
            { label: 'Kits Short', value: tabCounts.short, color: '#ea580c', bg: 'rgba(234,88,12,0.07)', border: 'rgba(234,88,12,0.18)', icon: null },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl p-3 space-y-1"
              style={{ background: stat.bg, border: `1px solid ${stat.border}` }}
            >
              <p className="text-[10px] font-500 uppercase tracking-wide" style={{ color: stat.color }}>{stat.label}</p>
              <p className="text-xl font-700 leading-none" style={{ color: stat.color }}>{stat.value.toLocaleString()}</p>
            </div>
          ))}
        </div>

        {/* Search bar */}
        <div className="relative w-full sm:w-80">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: '#94a3b8' }}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search kits by name, serial, CC no…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 text-xs rounded-xl outline-none"
            style={{ background: '#ffffff', border: '1px solid #e2e8f0', color: '#1a2332' }}
          />
        </div>

        {/* Tab bar */}
        <div className="space-y-4">
          <div className="flex items-center gap-0 overflow-x-auto pb-1" style={{ borderBottom: '1px solid #e2e8f0' }}>
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-600 whitespace-nowrap transition-all relative flex-shrink-0"
                  style={isActive
                    ? { color: tab.color, borderBottom: `2px solid ${tab.color}`, marginBottom: '-1px' }
                    : { color: '#64748b', borderBottom: '2px solid transparent', marginBottom: '-1px' }
                  }
                >
                  {tab.label}
                  <span
                    className="text-[9px] font-700 px-1.5 py-0.5 rounded-full"
                    style={isActive ? tab.activeStyle : tab.badgeStyle}
                  >
                    {tabCounts[tab.key]}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Count + per-page */}
          <div className="flex items-center justify-between">
            <p className="text-xs font-500" style={{ color: '#64748b' }}>
              {totalKits === 0
                ? 'No kits found'
                : <>Showing <span className="font-700" style={{ color: activeTabMeta.color }}>{start + 1}–{end}</span> of{' '}
                  <span className="font-700" style={{ color: activeTabMeta.color }}>{totalKits}</span> kits</>
              }
            </p>
            <div className="flex items-center gap-2">
              <label className="text-xs" style={{ color: '#64748b' }}>Per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setPage(1); }}
                className="text-xs rounded-lg px-2 py-1 outline-none"
                style={{ background: '#ffffff', border: '1px solid #e2e8f0', color: '#1a2332' }}
              >
                {ITEMS_PER_PAGE_OPTIONS.map((opt) => (
                  <option key={`pp-${opt}`} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Empty state */}
          {visibleKits.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 rounded-xl" style={{ border: '1px dashed #e2e8f0', background: '#f8fafc' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth={1.5} className="w-12 h-12 mb-3">
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 7V5a2 2 0 00-4 0v2M8 7V5a2 2 0 00-4 0v2" />
              </svg>
              <p className="text-sm font-600" style={{ color: '#94a3b8' }}>No kits found</p>
              {search && <p className="text-xs mt-1" style={{ color: '#cbd5e1' }}>Try a different search term</p>}
            </div>
          )}

          {/* Kit grid */}
          {visibleKits.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {visibleKits.map((kit) => {
                const badge = getStatusBadge(kit.status);
                const expiryColor = getExpiryTextColor(kit.status);
                return (
                  <div
                    key={kit.id}
                    className="rounded-xl border p-4 space-y-2 kit-card-hover cursor-pointer fade-in"
                    style={{ background: '#ffffff', borderColor: '#e2e8f0' }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-0.5 flex-1 min-w-0">
                        <p className="text-[10px] truncate" style={{ color: '#64748b' }}>
                          Serial: {kit.serialNo}
                        </p>
                        <p className="text-[10px]" style={{ color: '#64748b' }}>
                          CC No: {kit.ccNo}
                        </p>
                      </div>
                      <span
                        className="text-[9px] font-600 px-1.5 py-0.5 rounded-full flex-shrink-0"
                        style={{ background: badge.bg, color: badge.color, border: `1px solid ${badge.border}` }}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <p className="text-sm font-700 leading-snug" style={{ color: '#0f2744' }}>
                      {kit.kitName}
                    </p>
                    <p className="text-xs font-500" style={{ color: expiryColor }}>
                      Expiry: {formatExpiry(kit.expiry)}
                    </p>
                    <p className="text-[10px] truncate" style={{ color: '#94a3b8' }}>
                      CC: {kit.ccName}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1.5 pt-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ color: '#64748b', background: '#ffffff', border: '1px solid #e2e8f0' }}
              >
                <ChevronLeft size={15} />
              </button>
              {pageNumbers.map((pg, idx) =>
                pg === '...' ? (
                  <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-xs" style={{ color: '#94a3b8' }}>…</span>
                ) : (
                  <button
                    key={`page-${pg}`}
                    onClick={() => setPage(pg as number)}
                    className="w-8 h-8 text-xs rounded-lg transition-colors font-600"
                    style={page === pg
                      ? { background: activeTabMeta.color, color: 'white', border: `1px solid ${activeTabMeta.color}` }
                      : { background: '#ffffff', color: '#64748b', border: '1px solid #e2e8f0' }
                    }
                  >
                    {pg}
                  </button>
                )
              )}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ color: '#64748b', background: '#ffffff', border: '1px solid #e2e8f0' }}
              >
                <ChevronRight size={15} />
              </button>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
